/**
 * ConfirmDialog — Accessible confirmation modal
 */
import { WarningAmberRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import React from 'react';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  content: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
  severity?: 'error' | 'warning' | 'info';
}

const severityColors = {
  error: { icon: '#ef4444', bg: '#fee2e2', btn: 'error' as const },
  warning: { icon: '#f59e0b', bg: '#fef3c7', btn: 'warning' as const },
  info: { icon: '#3b82f6', bg: '#dbeafe', btn: 'primary' as const },
};

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  content,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  loading = false,
  severity = 'error',
}) => {
  const colors = severityColors[severity];

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
    >
      <DialogTitle id="confirm-dialog-title" sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: colors.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <WarningAmberRounded sx={{ fontSize: 20, color: colors.icon }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent id="confirm-dialog-description" sx={{ pt: 1.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
          {content}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
        <Button variant="outlined" onClick={onCancel} disabled={loading} sx={{ flex: 1 }}>
          {cancelText}
        </Button>
        <Button
          variant="contained"
          color={colors.btn}
          onClick={onConfirm}
          disabled={loading}
          autoFocus
          sx={{ flex: 1 }}
        >
          {loading ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
